import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  VdiTicketSubmitRequest,
  VdiTicketSubmitResponse,
  ProvisioningStatusResponse,
  ConnectionDetailsResponse,
  RetryProvisioningResponse,
  ProvisioningErrorResponse
} from '../interface/avd-provisioning';

/**
 * Service for AVD ticket provisioning operations
 * Handles communication with backend provisioning APIs
 */
@Injectable({
  providedIn: 'root'
})
export class TicketProvisioningService {
  private readonly apiBaseUrl = '/api/provisioning';

  constructor(private http: HttpClient) { }

  /**
   * Submit a VDI ticket and trigger AVD provisioning
   */
  submitVdiTicket(request: VdiTicketSubmitRequest): Observable<VdiTicketSubmitResponse> {
    const payload = {
      ...request,
      scheduleStartUtc: request.scheduleStartUtc instanceof Date
        ? request.scheduleStartUtc.toISOString()
        : request.scheduleStartUtc,
      scheduleEndUtc: request.scheduleEndUtc instanceof Date
        ? request.scheduleEndUtc.toISOString()
        : request.scheduleEndUtc,
      repeatUntilUtc: request.repeatUntilUtc instanceof Date
        ? request.repeatUntilUtc.toISOString()
        : request.repeatUntilUtc
    };

    return this.http.post<VdiTicketSubmitResponse>(
      `${this.apiBaseUrl}/submit`,
      payload
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get provisioning status for a ticket by ticket number
   */
  getProvisioningStatus(ticketNumber: string): Observable<ProvisioningStatusResponse> {
    return this.http.get<ProvisioningStatusResponse>(
      `${this.apiBaseUrl}/status/${ticketNumber}`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get connection details for a successfully provisioned ticket by ticket number
   */
  getConnectionDetails(ticketNumber: string): Observable<ConnectionDetailsResponse> {
    return this.http.get<ConnectionDetailsResponse>(
      `${this.apiBaseUrl}/connection/${ticketNumber}`
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Retry a failed provisioning attempt by ticket number
   */
  retryProvisioning(ticketNumber: string): Observable<RetryProvisioningResponse> {
    return this.http.post<RetryProvisioningResponse>(
      `${this.apiBaseUrl}/retry/${ticketNumber}`,
      {}
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Check if credentials are about to expire (within 7 days)
   */
  areCredentialsExpiringSoon(expiryDate: string): boolean {
    const expiry = new Date(expiryDate);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 7 && daysUntilExpiry > 0;
  }

  /**
   * Check if credentials have expired
   */
  areCredentialsExpired(expiryDate: string | undefined): boolean {
    if (!expiryDate) return true;
    return new Date(expiryDate) < new Date();
  }

  /**
   * Format provisioning date for display
   */
  formatDate(dateString: string | undefined): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Hong_Kong'
    });
  }

  /**
   * Get human-readable status message
   */
  getStatusMessage(status: string): string {
    switch (status) {
      case 'Pending':
        return 'Provisioning has not started yet. Please check back in a few moments.';
      case 'InProgress':
        return 'Your virtual desktop is being provisioned. This usually takes 5-10 minutes.';
      case 'Success':
        return 'Your virtual desktop has been successfully provisioned!';
      case 'Failed':
        return 'Provisioning failed. You can retry or contact support for assistance.';
      default:
        return 'Unknown status';
    }
  }

  /**
   * Get status color for UI badges
   */
  getStatusColor(status: string): 'default' | 'processing' | 'success' | 'error' | 'warning' {
    switch (status) {
      case 'Pending':
        return 'default';
      case 'InProgress':
        return 'processing';
      case 'Success':
        return 'success';
      case 'Failed':
        return 'error';
      default:
        return 'default';
    }
  }

  /**
   * Validate VDI ticket request before submission
   */
  validateTicketRequest(request: VdiTicketSubmitRequest): string[] {
    const errors: string[] = [];

    if (!request.requesterEmail || !this.isValidEmail(request.requesterEmail)) {
      errors.push('Valid requester email is required');
    }

    if (!request.scheduleStartUtc) {
      errors.push('Schedule start date is required');
    }

    if (!request.scheduleEndUtc) {
      errors.push('Schedule end date is required');
    }

    if (request.scheduleStartUtc && request.scheduleEndUtc) {
      const start = new Date(request.scheduleStartUtc);
      const end = new Date(request.scheduleEndUtc);
      if (end <= start) {
        errors.push('Schedule end date must be after start date');
      }

      const duration = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
      if (duration > 31) {
        errors.push('Schedule duration cannot exceed 31 days');
      }
    }

    if (!request.softwareSet) {
      errors.push('Software set is required');
    }

    if (!request.justification || request.justification.trim().length < 10) {
      errors.push('Justification must be at least 10 characters');
    }

    if (request.isRepeated && !request.repeatUntilUtc) {
      errors.push('Repeat until date is required for repeated schedules');
    }

    return errors;
  }

  /**
   * Email validation helper
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Error handling
   */
  private handleError(error: any): Observable<never> {
    let errorMessage = 'An error occurred while processing your request';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = error.error.message;
    } else {
      // Server-side error
      if (error.status === 401) {
        errorMessage = 'Authentication required. Please sign in.';
      } else if (error.status === 403) {
        errorMessage = 'You do not have permission to perform this action.';
      } else if (error.status === 404) {
        errorMessage = 'Ticket not found.';
      } else if (error.error && error.error.message) {
        errorMessage = error.error.message;
      } else {
        errorMessage = `Error: ${error.status} - ${error.statusText}`;
      }
    }

    console.error('TicketProvisioningService Error:', error);
    return throwError(() => new Error(errorMessage));
  }
}























